import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useGestock } from './GestockContext';
import { clearViewAs } from '../../Auth/authSlice';
import MonthSelector from './MonthSelector';

const NAV_ITEMS = [
  { id: 'dashboard',      icon: '📊', label: 'Tab. de Bord' },
  { id: 'sous-douane',    icon: '🔒', label: 'Douane' },
  { id: 'libre-pratique', icon: '✅', label: 'Libre P.' },
  { id: 'commandes',      icon: '📋', label: 'Commande' },
  { id: 'stations',       icon: '⛽', label: 'Stations' },
  { id: 'journal',        icon: '📜', label: 'Journal' },
  { id: 'rapport',        icon: '📄', label: 'Rapport' },
  { id: 'parametres',     icon: '⚙️', label: 'Paramètres', adminOnly: true },
];

// Barre du bas (téléphone) : sans Tableau de bord ni Rapport, toujours
// accessibles via le menu latéral (☰)
const BOTTOM_NAV_HIDDEN = new Set(['dashboard', 'rapport', 'parametres']);

export default function GLayout({ children }) {
  const { terminal, setTerminal, produit, setProduit, activePage, setActivePage } = useGestock();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { displayName, isAdmin } = useSelector((state) => state.auth);
  // Onglets réservés aux admins (Paramètres) masqués pour les autres comptes
  const navItems = NAV_ITEMS.filter(item => !item.adminOnly || isAdmin);
  const [clock, setClock] = useState('');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  useEffect(() => {
    const tick = () => setClock(new Date().toLocaleTimeString('fr-FR', { hour12: false }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const handleNavItem = (id) => {
    setActivePage(id);
    setMobileSidebarOpen(false);
  };

  return (
    <div className="app-layout">

      {/* ── Header ── */}
      <header className="header">
        <div className="header-logo">
          <button
            className="hamburger"
            onClick={() => setMobileSidebarOpen(o => !o)}
            aria-label="Menu"
          >
            <span /><span /><span />
          </button>
          <div className="logo-mark">⛽</div>
          <div>
            <div className="logo-text">OILMED</div>
            <div className="logo-sub">GESTION STOCK CARBURANT</div>
          </div>
        </div>
        <div className="header-right">
          <div className="live-badge">
            <div className="live-dot" />
            LIVE
          </div>
          <div className="clock">{clock}</div>
        </div>
      </header>

      {/* ── Mobile sidebar backdrop ── */}
      {mobileSidebarOpen && (
        <div className="sidebar-backdrop" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* ── Sidebar (desktop static / mobile overlay) ── */}
      <aside className={`sidebar${mobileSidebarOpen ? ' mobile-open' : ''}`}>
        <div className="sidebar-section">
          <div className="sidebar-label">Navigation</div>
          {navItems.map(item => (
            <button
              key={item.id}
              className={`nav-item ${activePage === item.id ? 'active' : ''}`}
              onClick={() => handleNavItem(item.id)}
            >
              <span>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
        <div className="sidebar-section" style={{ marginTop: 'auto' }}>
          {displayName && (
            <div style={{ padding: '6px 16px 10px', fontSize: 11, color: 'var(--muted)', fontWeight: 600, letterSpacing: 0.5 }}>
              👤 {displayName}
            </div>
          )}
          <button className="nav-item" style={{ color: 'var(--muted)' }} onClick={() => { dispatch(clearViewAs()); navigate('/menu'); }}>
            <span>←</span>
            Menu
          </button>
        </div>
      </aside>

      {/* ── Main ── */}
      <div style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* Sélecteur terminal */}
        <div className="selector-bar">
          {['SEP', 'LP'].map(t => (
            <button
              key={t}
              className={`sel-tab ${terminal === t ? (t === 'SEP' ? 'active-sep' : 'active-lp-t') : ''}`}
              onClick={() => setTerminal(t)}
            >
              <span className="sel-dot" style={{ background: t === 'SEP' ? 'var(--blue)' : 'var(--purple)' }} />
              🏭 Terminal {t}
            </button>
          ))}
        </div>

        {/* Sélecteur produit */}
        <div className="selector-bar" style={{ borderTop: 'none' }}>
          <button
            className={`sel-tab ${produit === 'essence' ? 'active-essence' : ''}`}
            onClick={() => setProduit('essence')}
          >
            <span className="sel-dot" style={{ background: 'var(--blue)' }} />
            💧 Super
          </button>
          <button
            className={`sel-tab ${produit === 'go' ? 'active-go' : ''}`}
            onClick={() => setProduit('go')}
          >
            <span className="sel-dot" style={{ background: 'var(--orange)' }} />
            🛢 Go
          </button>
        </div>

        {/* Sélecteur de mois — s'applique à toutes les tables d'historique */}
        <MonthSelector />

        <div className="page-content">{children}</div>
      </div>

      {/* ── Bottom Nav mobile ── */}
      <nav className="bottom-nav">
        {navItems.filter(item => !BOTTOM_NAV_HIDDEN.has(item.id)).map(item => (
          <button
            key={item.id}
            className={`bn-item ${activePage === item.id ? 'active' : ''}`}
            onClick={() => setActivePage(item.id)}
          >
            <span className="bn-icon">{item.icon}</span>
            {item.label}
          </button>
        ))}
        <button className="bn-item" style={{ color: 'var(--muted)' }} onClick={() => { dispatch(clearViewAs()); navigate('/menu'); }}>
          <span className="bn-icon">←</span>
          Menu
        </button>
      </nav>
    </div>
  );
}
